<template>
  <div class="notification-preferences">
    <div v-if="loading" class="np-loading">
      <hs-card class="np-loading__card" variant="flat">
        <div class="np-loading__header">
          <hs-skeleton variant="circle" width="2.75rem" height="2.75rem" />
          <div>
            <hs-skeleton width="14rem" />
            <hs-skeleton width="20rem" />
          </div>
        </div>
        <hs-skeleton variant="card" />
        <hs-skeleton variant="card" />
      </hs-card>
    </div>

    <hs-alert v-else-if="error" tone="danger" title="Unable to load notifications">
      <span>{{ error }}</span>
      <hs-button class="np-inline-action" variant="ghost" size="sm" icon="refresh-cw" @click="loadPreferences">
        Retry
      </hs-button>
    </hs-alert>

    <div v-else class="np-content">
      <hs-card class="np-hero" variant="flat">
        <div class="np-hero__copy">
          <hs-icon name="bell" tone="primary" soft :size="20" />
          <div>
            <p class="np-eyebrow">Notification Preferences</p>
            <h3>Control how updates reach you</h3>
            <span>
              Configure push and email channels. Store-level preferences override user-level preferences.
            </span>
          </div>
        </div>
        <div class="np-hero__stats">
          <div class="np-stat">
            <span>Topics</span>
            <strong>{{ implementedTopicCount }}</strong>
          </div>
          <div class="np-stat">
            <span>Push On</span>
            <strong>{{ pushEnabledCount }}</strong>
          </div>
          <div class="np-stat">
            <span>Email On</span>
            <strong>{{ emailEnabledCount }}</strong>
          </div>
        </div>
      </hs-card>

      <div class="np-action-grid">
        <hs-card v-if="!webNotificationPermissionGranted" class="np-action-card" variant="soft">
          <div class="np-action-card__copy">
            <hs-icon name="bell" tone="primary" soft :size="18" />
            <div>
              <h4>Enable web notifications</h4>
              <p>Allow real-time updates while the browser is in the background.</p>
            </div>
          </div>
          <hs-button icon="bell" @click="requestWebNotificationPermission">
            Enable
          </hs-button>
        </hs-card>

        <hs-card class="np-action-card" variant="soft">
          <div class="np-action-card__copy">
            <hs-icon name="send" tone="info" soft :size="18" />
            <div>
              <h4>Test push notification</h4>
              <p>Send a test notification to verify this device is ready.</p>
            </div>
          </div>
          <hs-button
            icon="send"
            :loading="testingNotification"
            :disabled="testingNotification || rateLimited"
            @click="sendTestNotification"
          >
            {{ testingNotification ? "Sending..." : "Send Test" }}
          </hs-button>
        </hs-card>
      </div>

      <hs-alert
        v-if="rateLimited"
        tone="warning"
        title="Test notification rate limited"
        message="Please wait before sending another test notification."
      />

      <hs-table-shell
        title="Notification types"
        description="Choose which channel should be active for each notification topic."
      >
        <hs-data-table
          :headers="notificationHeaders"
          :data="sortedTopics"
          :header-key-mapping="notificationHeaderKeyMapping"
        >
          <template #cell-notification="{ row }">
            <div class="np-topic">
              <div class="np-topic__title-row">
                <strong>{{ row.label }}</strong>
                <hs-badge v-if="row.implemented === false" tone="warning" size="sm">
                  Planned
                </hs-badge>
                <hs-tooltip :text="row.recipients || 'Store owner and relevant users'" placement="right">
                  <span class="np-topic__info">
                    <hs-icon name="info" :size="14" />
                  </span>
                </hs-tooltip>
              </div>
              <span>{{ row.description }}</span>
            </div>
          </template>

          <template #cell-push="{ row }">
            <div class="np-channel-cell">
              <hs-toggle
                v-if="row.implemented !== false"
                :model-value="getPreference(row.key, 'PUSH')"
                @update:modelValue="updatePreference(row.key, 'PUSH', $event)"
              />
              <hs-badge v-else tone="neutral" size="sm">N/A</hs-badge>
            </div>
          </template>

          <template #cell-email="{ row }">
            <div class="np-channel-cell">
              <hs-toggle
                v-if="row.emailAllowed && row.implemented !== false"
                :model-value="getPreference(row.key, 'EMAIL')"
                @update:modelValue="updatePreference(row.key, 'EMAIL', $event)"
              />
              <hs-badge v-else tone="neutral" size="sm">N/A</hs-badge>
            </div>
          </template>
        </hs-data-table>

        <template #footer>
          <div class="np-savebar">
            <span>{{ hasChanges ? "Unsaved changes" : "Preferences are up to date" }}</span>
            <div class="np-savebar__actions">
              <hs-button
                variant="secondary"
                icon="rotate-ccw"
                :disabled="saving || !hasChanges"
                @click="resetPreferences"
              >
                Reset
              </hs-button>
              <hs-button
                icon="save"
                :loading="saving"
                :disabled="saving || !hasChanges"
                @click="savePreferences"
              >
                {{ saving ? "Saving..." : "Save Changes" }}
              </hs-button>
            </div>
          </div>
        </template>
      </hs-table-shell>
    </div>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import api from "@/helper/api";
import { useWebNotifications } from "@/composables/useWebNotifications";
import {
  HsAlert,
  HsBadge,
  HsButton,
  HsCard,
  HsDataTable,
  HsIcon,
  HsSkeleton,
  HsTableShell,
  HsToggle,
  HsTooltip,
} from "@/components/ui";

export default {
  name: "NotificationPreferences",
  components: {
    HsAlert,
    HsBadge,
    HsButton,
    HsCard,
    HsDataTable,
    HsIcon,
    HsSkeleton,
    HsTableShell,
    HsToggle,
    HsTooltip,
  },
  props: {
    storeId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      saving: false,
      testingNotification: false,
      rateLimited: false,
      preferences: {},
      originalPreferences: {},
      lastTestNotificationTime: null,
      webNotifications: null,
      notificationHeaders: ["Notification", "Push", "Email"],
      notificationHeaderKeyMapping: {
        Notification: "label",
        Push: "push",
        Email: "email",
      },
      // Topic definitions with user-friendly labels
      topicDefinitions: {
        EMAIL_VERIFICATION_SENT: {
          label: "Email Verification Sent",
          description: "When a verification email is sent",
          emailAllowed: false,
          recipients: "User who requested verification",
          implemented: true,
        },
        EMAIL_VERIFICATION_FAILED: {
          label: "Email Verification Failed",
          description: "When email verification fails",
          emailAllowed: false,
          recipients: "User who requested verification",
          implemented: true,
        },
        PERIODIC_REPORT_SENT: {
          label: "Periodic Report Sent",
          description: "When a periodic report is sent",
          emailAllowed: false,
          recipients: "Store owner",
          implemented: true,
        },
        PERIODIC_REPORT_FAILED: {
          label: "Periodic Report Failed",
          description: "When periodic report sending fails",
          emailAllowed: false,
          recipients: "Store owner",
          implemented: true,
        },
        STORE_DETAILS_CHANGE_REQUEST_SUBMITTED: {
          label: "Store Details Change Request Submitted",
          description: "When a store details change request is submitted",
          emailAllowed: true,
          recipients:
            "Store owner and SUPER_ADMINS",
          implemented: true,
        },
        STORE_DETAILS_CHANGE_REQUEST_APPROVED: {
          label: "Store Details Change Request Approved",
          description: "When a store details change request is approved",
          emailAllowed: true,
          recipients:
            "Store owner and SUPER_ADMINS",
          implemented: true,
        },
        STORE_DETAILS_CHANGE_REQUEST_REJECTED: {
          label: "Store Details Change Request Rejected",
          description: "When a store details change request is rejected",
          emailAllowed: true,
          recipients:
            "Store owner and SUPER_ADMINS",
          implemented: true,
        },
        EBM_SETTING_CHANGE_REQUEST_SUBMITTED: {
          label: "EBM Setting Change Request Submitted",
          description: "When an EBM setting change request is submitted",
          emailAllowed: true,
          recipients:
            "Store owner, users with CAN_MANAGE_RRA_EBM_DATA permission, and SUPER_ADMINS",
          implemented: true,
        },
        EBM_SETTING_CHANGE_REQUEST_APPROVED: {
          label: "EBM Setting Change Request Approved",
          description: "When an EBM setting change request is approved",
          emailAllowed: true,
          recipients:
            "Store owner, users with CAN_MANAGE_RRA_EBM_DATA permission, and SUPER_ADMINS",
          implemented: true,
        },
        EBM_SETTING_CHANGE_REQUEST_REJECTED: {
          label: "EBM Setting Change Request Rejected",
          description: "When an EBM setting change request is rejected",
          emailAllowed: true,
          recipients:
            "Store owner, users with CAN_MANAGE_RRA_EBM_DATA permission, and SUPER_ADMINS",
          implemented: true,
        },
        EBM_TRANSACTION_FAILED: {
          label: "EBM Transaction Failed",
          description: "When an EBM transaction fails",
          emailAllowed: true,
          recipients:
            "Store owner and users with CAN_WRITE_SALE_CARTS permission",
          implemented: false,
        },
        EBM_SYNC_COMPLETED: {
          label: "EBM Sync Completed",
          description: "When EBM sync completes successfully",
          emailAllowed: true,
          recipients:
            "Store owner and users with CAN_WRITE_EBM_SETTINGS permission",
          implemented: false,
        },
        EBM_SYNC_FAILED: {
          label: "EBM Sync Failed",
          description: "When EBM sync fails",
          emailAllowed: true,
          recipients:
            "Store owner and users with CAN_WRITE_EBM_SETTINGS permission",
          implemented: false,
        },
        INVOICE_REFUNDED: {
          label: "Invoice Refunded",
          description: "When an invoice is refunded",
          emailAllowed: true,
          recipients:
            "Store owner and users with CAN_WRITE_SALE_CARTS permission",
          implemented: true,
        },
        RECEIPT_GENERATION_COMPLETED: {
          label: "Receipt Generation Completed",
          description: "When receipt generation completes successfully",
          emailAllowed: true,
          recipients:
            "Store owner and users with CAN_WRITE_SALE_CARTS permission",
          implemented: false,
        },
        RECEIPT_GENERATION_FAILED: {
          label: "Receipt Generation Failed",
          description: "When receipt generation fails",
          emailAllowed: true,
          recipients:
            "Store owner and users with CAN_WRITE_SALE_CARTS permission",
          implemented: false,
        },
      },
    };
  },
  computed: {
    sortedTopics() {
      return Object.keys(this.topicDefinitions)
        .map((key) => ({
          key,
          ...this.topicDefinitions[key],
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    hasChanges() {
      return (
        JSON.stringify(this.preferences) !==
        JSON.stringify(this.originalPreferences)
      );
    },
    implementedTopicCount() {
      return this.sortedTopics.filter((topic) => topic.implemented !== false).length;
    },
    pushEnabledCount() {
      return this.sortedTopics.filter(
        (topic) => topic.implemented !== false && this.getPreference(topic.key, "PUSH")
      ).length;
    },
    emailEnabledCount() {
      return this.sortedTopics.filter(
        (topic) =>
          topic.implemented !== false &&
          topic.emailAllowed &&
          this.getPreference(topic.key, "EMAIL")
      ).length;
    },
    webNotificationPermissionGranted() {
      return this.webNotifications?.permissionGranted || false;
    },
  },
  watch: {
    storeId() {
      this.loadPreferences();
    },
  },
  created() {
    // Initialize web notifications composable
    this.webNotifications = useWebNotifications();
  },
  mounted() {
    this.loadPreferences();
  },
  methods: {
    async loadPreferences() {
      this.loading = true;
      this.error = null;
      try {
        const endpoint = this.storeId
          ? api.get_store_notification_preferences(this.storeId)
          : api.get_user_notification_preferences;
        const response = await http.get(endpoint);
        this.preferences = response.data?.preferences || {};
        this.originalPreferences = JSON.parse(JSON.stringify(this.preferences));
      } catch (error) {
        console.error("Failed to load notification preferences:", error);
        this.error =
          error.response?.data?.error?.message ||
          "Failed to load notification preferences. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    getPreference(topicKey, channel) {
      const topicPrefs = this.preferences[topicKey];
      if (!topicPrefs) {
        // Default: PUSH enabled, EMAIL enabled (unless email not allowed for topic)
        return (
          channel === "PUSH" ||
          this.topicDefinitions[topicKey]?.emailAllowed !== false
        );
      }
      return topicPrefs[channel] !== false;
    },
    updatePreference(topicKey, channel, enabled) {
      // Create a new object to ensure reactivity
      const newPreferences = { ...this.preferences };
      if (!newPreferences[topicKey]) {
        newPreferences[topicKey] = {};
      }
      newPreferences[topicKey] = {
        ...newPreferences[topicKey],
        [channel]: enabled,
      };
      this.preferences = newPreferences;
    },
    async savePreferences() {
      this.saving = true;
      try {
        const endpoint = this.storeId
          ? api.update_store_notification_preferences(this.storeId)
          : api.update_user_notification_preferences;
        const response = await http.put(endpoint, {
          preferences: this.preferences,
        });
        this.preferences = response.data?.preferences || {};
        this.originalPreferences = JSON.parse(JSON.stringify(this.preferences));
        toast.success("Notification preferences saved successfully");
      } catch (error) {
        console.error("Failed to save notification preferences:", error);
        toast.error(
          error.response?.data?.error?.message ||
            "Failed to save notification preferences. Please try again."
        );
      } finally {
        this.saving = false;
      }
    },
    resetPreferences() {
      this.preferences = JSON.parse(JSON.stringify(this.originalPreferences));
      toast.info("Preferences reset to saved values");
    },
    async requestWebNotificationPermission() {
      if (this.webNotifications) {
        await this.webNotifications.requestPermission();
      }
    },
    async sendTestNotification() {
      // Rate limiting check: at least 1 second between requests
      const now = Date.now();
      if (
        this.lastTestNotificationTime &&
        now - this.lastTestNotificationTime < 1000
      ) {
        this.rateLimited = true;
        setTimeout(() => {
          this.rateLimited = false;
        }, 1000 - (now - this.lastTestNotificationTime));
        return;
      }

      this.testingNotification = true;
      this.rateLimited = false;
      this.lastTestNotificationTime = now;

      try {
        await http.post(api.test_push_notification, {
          title: "Test Notification",
          body: "This is a test notification from HovaStore",
        });
        toast.success("Test notification sent successfully!");

        // Show browser notification if permission granted
        if (this.webNotificationPermissionGranted && this.webNotifications) {
          this.webNotifications.showTestNotification(
            "Test Notification",
            "This is a test notification from HovaStore"
          );
        }
      } catch (error) {
        console.error("Failed to send test notification:", error);
        const errorMessage =
          error.response?.data?.error?.message || error.response?.data?.message;
        if (error.response?.status === 429) {
          this.rateLimited = true;
          toast.error(
            "Rate limit exceeded. Please wait before sending another test notification."
          );
        } else {
          toast.error(
            errorMessage ||
              "Failed to send test notification. Please try again."
          );
        }
      } finally {
        this.testingNotification = false;
      }
    },
  },
};
</script>

<style scoped>
.notification-preferences {
  display: grid;
  gap: 1rem;
  min-width: 0;
  width: 100%;
}

.np-content {
  display: grid;
  gap: 1rem;
  min-width: 0;
}

.np-loading {
  display: grid;
}

.np-loading__card {
  display: grid;
  gap: 1rem;
}

.np-loading__header {
  align-items: center;
  display: flex;
  gap: 0.85rem;
}

.np-loading__header > div:last-child {
  display: grid;
  flex: 1;
  gap: 0.55rem;
  min-width: 0;
}

.np-inline-action {
  margin-left: 0.5rem;
}

.np-hero {
  align-items: center;
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1fr) auto;
}

.np-hero__copy {
  align-items: center;
  display: flex;
  gap: 0.9rem;
  min-width: 0;
}

.np-hero__copy > :deep(.ui-icon),
.np-action-card__copy > :deep(.ui-icon) {
  align-self: center;
  display: inline-flex;
  flex: 0 0 2.5rem;
  height: 2.5rem;
  place-content: center;
  place-items: center;
  width: 2.5rem;
}

.np-eyebrow {
  color: var(--hs-primary, #3293ec);
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.08em;
  margin: 0 0 0.18rem;
  text-transform: uppercase;
}

.np-hero h3,
.np-action-card h4 {
  color: var(--hs-text, #0f172a);
  font-size: 1rem;
  font-weight: 950;
  line-height: 1.2;
  margin: 0;
}

.np-hero span,
.np-action-card p {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.5;
  margin: 0.22rem 0 0;
}

.np-hero__stats {
  display: grid;
  gap: 0.55rem;
  grid-template-columns: repeat(3, minmax(5.5rem, 1fr));
}

.np-stat {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 62%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  padding: 0.75rem;
}

.np-stat span {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.68rem;
  font-weight: 950;
  letter-spacing: 0.07em;
  margin: 0;
  text-transform: uppercase;
}

.np-stat strong {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 1.2rem;
  font-weight: 950;
  line-height: 1;
  margin-top: 0.35rem;
}

.np-action-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 22rem), 1fr));
}

.np-action-card {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.np-action-card__copy {
  align-items: center;
  display: flex;
  gap: 0.8rem;
  min-width: 0;
}

.np-topic {
  display: grid;
  gap: 0.25rem;
  max-width: 36rem;
  min-width: 0;
}

.np-topic__title-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.np-topic strong {
  color: var(--hs-text, #0f172a);
  font-size: 0.88rem;
  font-weight: 950;
}

.np-topic > span {
  color: var(--hs-muted, #64748b);
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.45;
}

.np-topic__info {
  align-items: center;
  color: var(--hs-muted, #64748b);
  display: inline-flex;
  height: 1.25rem;
  justify-content: center;
  width: 1.25rem;
}

.np-topic__info :deep(.ui-icon) {
  align-items: center;
  display: inline-flex;
  justify-content: center;
}

.np-channel-cell {
  align-items: center;
  display: flex;
  justify-content: center;
  min-width: 5rem;
}

.np-savebar {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: space-between;
}

.np-savebar > span {
  color: var(--hs-muted, #64748b);
  font-size: 0.82rem;
  font-weight: 800;
}

.np-savebar__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

@media (max-width: 860px) {
  .np-hero {
    grid-template-columns: 1fr;
  }

  .np-hero__stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .np-hero__copy,
  .np-action-card,
  .np-action-card__copy {
    align-items: flex-start;
    flex-direction: column;
  }

  .np-hero__stats {
    grid-template-columns: 1fr;
  }

  .np-action-card {
    align-items: stretch;
  }

  .np-savebar,
  .np-savebar__actions {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }
}
</style>
